import { Suspense, useCallback, useContext, useEffect, useRef, useState } from 'react'
import { Outlet } from 'react-router-dom'
import Sidebar from './components/sidebar'
import Header from './components/Header'
import Footer from './components/Footer'
import { useSidebarCollapsed } from '../hooks/useSidebarCollapsed'
import { AuthContext } from '../context/auth-context'
import { useMediaQuery } from '../hooks/useMediaQuery'

const DESKTOP_BREAKPOINT = '(min-width: 1024px)'

const AppLayout = () => {
    const auth = useContext(AuthContext) as { user: { id?: string | null } | null }
    const authUser = auth?.user ?? null
    const { collapsed, setCollapsed } = useSidebarCollapsed(authUser?.id)
    const isDesktop = useMediaQuery(DESKTOP_BREAKPOINT)
    const [isMobileOpen, setIsMobileOpen] = useState(false)

    const collapsedRef = useRef(collapsed)
    useEffect(() => {
        collapsedRef.current = collapsed
    }, [collapsed])

    useEffect(() => {
        if (isDesktop) {
            setIsMobileOpen(false)
        }
    }, [isDesktop])

    useEffect(() => {
        const handleShortcut = (event: KeyboardEvent) => {
            if (event.key === '\\' && event.shiftKey) {
                event.preventDefault()
                setCollapsed(!collapsedRef.current)
            }
        }

        window.addEventListener('keydown', handleShortcut)

        return () => {
            window.removeEventListener('keydown', handleShortcut)
        }
    }, [setCollapsed])

    const toggleCollapse = useCallback(() => {
        setCollapsed(!collapsedRef.current)
    }, [setCollapsed])

    const toggleMobile = useCallback(() => {
        setIsMobileOpen((prev) => !prev)
    }, [])

    const closeMobile = useCallback(() => {
        setIsMobileOpen(false)
    }, [])

    const appName = import.meta.env.VITE_APP_NAME || 'Turba Superadmin'

    return (
        <div className="tw:flex tw:min-h-screen tw:w-full tw:bg-slate-950">
            <Sidebar
                appName={appName}
                collapsed={collapsed}
                isMobile={!isDesktop}
                isMobileOpen={isMobileOpen}
                onToggleCollapse={toggleCollapse}
                onToggleMobile={toggleMobile}
                onCloseMobile={closeMobile}
            />
            <main className="tw:flex tw:min-h-screen tw:w-full tw:flex-1 tw:flex-col tw:bg-slate-900/40 tw:overflow-hidden">
                <Header
                    appName={appName}
                    onToggleSidebar={toggleCollapse}
                    onToggleMobileSidebar={toggleMobile}
                    isSidebarCollapsed={collapsed}
                    isMobileSidebarOpen={isMobileOpen}
                    isDesktop={isDesktop}
                />
                <div className="tw:flex-1 tw:overflow-y-auto tw:overflow-x-hidden tw:px-4 tw:py-6 sm:tw:px-6">
                    <Suspense fallback={<div className="react-load" aria-label="Loading content" />}> 
                        <Outlet />
                    </Suspense>
                </div>
                <Footer />
            </main>
        </div>
    )
}

export default AppLayout
